import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { GraphNode, NodeKind, ALLOWED_CONNECTIONS } from './types';

interface CustomNodeProps extends NodeProps {
  data: GraphNode & {
    style: {
      color: string;
      bgColor: string;
      icon: string;
    };
  };
}

export default function CustomNode({ data, selected }: CustomNodeProps) {
  const { kind, label, status, style, metrics, props } = data;
  const missing = Boolean(props?._ui_missing);
  
  // Schema view nodes (IKUCategory, ReferenceCategory, Template) always need handles
  // for edges to render. ALLOWED_CONNECTIONS is for interactive connection validation only.
  const isSchemaNode = kind === 'IKUCategory' || kind === 'ReferenceCategory' || kind === 'Template';
  const allowedSources = ALLOWED_CONNECTIONS[kind as NodeKind] || [];
  const allowedTargets = Object.entries(ALLOWED_CONNECTIONS).filter(([_, connections]) =>
    connections.some(conn => conn.target === kind)
  ).map(([sourceKind]) => sourceKind);
  const showSourceHandle = isSchemaNode || allowedSources.length > 0;
  const showTargetHandle = isSchemaNode || allowedTargets.length > 0;

  const statusColor = {
    ok: 'bg-green-100 border-green-300',
    warn: 'bg-yellow-100 border-yellow-300', 
    error: 'bg-red-100 border-red-300',
    unknown: 'bg-gray-100 border-gray-300'
  }[status];

  const statusDot = {
    ok: 'bg-green-500',
    warn: 'bg-yellow-500',
    error: 'bg-red-500', 
    unknown: 'bg-gray-400'
  }[status];

  return (
    <div 
      className={`
        relative min-w-[150px] rounded-lg border-2 shadow-sm 
        ${selected ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
        ${missing ? 'border-dashed border-gray-300 opacity-80' : statusColor}
      `}
      style={{ backgroundColor: missing ? '#F9FAFB' : style.bgColor }}
    >
      {showTargetHandle && (
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-gray-400 border-2 border-white"
          style={{ left: -6 }}
        />
      )}

      {/* Node content */}
      <div className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">{style.icon}</span>
          <span className="font-medium text-sm truncate" title={label}>
            {label}
          </span>
          <div className={`w-2 h-2 rounded-full ${statusDot} ml-auto`} />
        </div>
        
        <div className="text-xs text-gray-600 mb-1">
          {kind}
        </div>

        {/* Metrics */}
        {metrics && (
          <div className="text-xs text-gray-500 space-y-0.5">
            {metrics.lastSeen && (
              <div>Last: {new Date(metrics.lastSeen).toLocaleTimeString()}</div>
            )}
            {metrics.rqps && (
              <div>RPS: {metrics.rqps}</div>
            )}
            {metrics.p95 && (
              <div>P95: {metrics.p95}ms</div>
            )}
          </div>
        )}
      </div>

      {showSourceHandle && (
        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 bg-gray-400 border-2 border-white"
          style={{ right: -6 }}
        />
      )}

      {/* Node type indicator */}
      <div 
        className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-xs flex items-center justify-center text-white font-bold"
        style={{ backgroundColor: style.color }}
        title={`${kind} node`}
      >
        {kind.charAt(0)}
      </div>
    </div>
  );
}
